import React from 'react'
import { Divider } from '@mui/material'

import ClientHeader from '../../../components/ClientHeader/ClientHeader'
import DetailsSummarySection from '../../../components/details/summary/DetailsSummarySection'
import PaymentAttachmentsCard from '../../../components/details/attachments/PaymentAttachmentsCard'
import ProgressSummaryCard from '../../../components/details/progress/ProgressSummaryCard'
import { REFUND_HEADER_CONFIG } from '../../show/shared/refundConstants'
import { REFUND_APPROVAL_TEXT } from '../../constants/refundApprovalText'
import { REFUND_APPROVAL_HISTORY_TAB } from '../../helpers/refundApprovalUtils'
import { UI_TEXT_ATTACHMENTS } from '../../../constants/uiText'
import {
  UI_COLOR_BORDER_DIVIDER,
  UI_COLOR_PRIMARY,
  UI_COLOR_TEXT_ICON
} from '../../../constants/uiColors'
import RefundChronologicalHistoryTab from '../RefundChronologicalHistoryTab'
import RefundApprovalTabsHeader from '../components/approval/RefundApprovalTabsHeader'
import RefundApprovalDetailActions from '../components/approval/RefundApprovalDetailActions'
import RefundNcrGuidanceNote from '../components/ncr/RefundNcrGuidanceNote'
import RefundApprovalProductsSectionMobile from './RefundApprovalProductsSectionMobile'

const MOBILE_SUMMARY_CARD_SX = {
  p: { xs: 3, sm: 3.5 },
  borderRadius: '10px',
  mb: 2.5
}

const MOBILE_SUMMARY_TITLE_SX = {
  fontSize: { xs: 17, sm: 17 },
  fontWeight: 800,
  lineHeight: 1.15,
  letterSpacing: 0,
  textTransform: 'uppercase'
}

const MOBILE_SUMMARY_PROGRESS_LABEL_SX = {
  fontSize: { xs: 16, sm: 16 },
  fontWeight: 800,
  color: UI_COLOR_TEXT_ICON,
  lineHeight: 1.25,
  letterSpacing: 0
}

const MOBILE_SUMMARY_PROGRESS_PERCENT_SX = {
  fontSize: { xs: 18, sm: 18 },
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: 0
}

const MOBILE_COLLAPSE_HEADER_SX = {
  px: 3
}

const MOBILE_COLLAPSE_TOGGLE_SX = {
  color: UI_COLOR_PRIMARY,
  p: 0.5
}

const MOBILE_CLIENT_HEADER_CARD_SX = {
  p: { xs: 2, sm: 2.25 },
  mb: 1.5
}

const MOBILE_CLIENT_HEADER_TITLE_SX = {
  mb: 1.25,
  fontSize: { xs: 20, sm: 20 },
  lineHeight: 1.15,
  letterSpacing: 0
}

const MOBILE_CLIENT_HEADER_STACK_SX = {
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1.15
}

const MOBILE_CLIENT_HEADER_ITEM_SX = {
  gap: 0.65,
  width: '100%',
  alignItems: 'center'
}

const MOBILE_CLIENT_HEADER_ICON_SX = {
  mt: 0,
  lineHeight: 0,
  flexShrink: 0
}

const MOBILE_CLIENT_HEADER_TEXT_SX = {
  fontSize: { xs: '13px !important', sm: '14px !important' },
  lineHeight: '1.15 !important',
  letterSpacing: '0 !important'
}

const MOBILE_CLIENT_HEADER_VALUE_SX = {
  ...MOBILE_CLIENT_HEADER_TEXT_SX,
  whiteSpace: 'normal !important',
  overflow: 'visible !important',
  textOverflow: 'clip !important',
  wordBreak: 'break-word'
}

const RefundApprovalDetailMobile = ({
  activity,
  activeTab,
  setActiveTab,
  detailTabs,
  canViewHistory,
  statusOption,
  headerData,
  summaryFields,
  fieldsToRender,
  paymentAttachments,
  progressPercent,
  progressSummaryMetrics,
  processingLines,
  processedLinesCount,
  allLinesRejected,
  canApproveCount,
  canWrite,
  canRegisterNcr,
  isFinalized,
  showLineActions,
  loading,
  isGeneratingPdf,
  copyTooltip,
  openScanModal,
  setLineToReject,
  handleCopyTable,
  handleDownloadTableCsv,
  handleDownloadTablePdf,
  openRejectConfirm,
  openApproveConfirm,
  openNcrDialog
}) => (
  <>
    <RefundApprovalTabsHeader
      tabs={detailTabs}
      activeTab={activeTab}
      onChange={setActiveTab}
      statusOption={statusOption}
      activity={activity}
      compact
    />

    {activeTab === REFUND_APPROVAL_HISTORY_TAB && canViewHistory ? (
      <RefundChronologicalHistoryTab
        refundUuid={activity?.uuid}
        rejectionReason={activity?.global_motive}
        mobileCards
      />
    ) : (
      <>
        <RefundApprovalDetailActions
          canWrite={canWrite}
          canRegisterNcr={canRegisterNcr}
          isFinalized={isFinalized}
          loading={loading}
          canApproveCount={canApproveCount}
          showLineActions={showLineActions}
          allLinesRejected={allLinesRejected}
          processedLinesCount={processedLinesCount}
          processingLinesCount={processingLines.length}
          isGeneratingPdf={isGeneratingPdf}
          onRejectClick={openRejectConfirm}
          onApproveClick={openApproveConfirm}
          onNcrClick={openNcrDialog}
          onExportCsv={handleDownloadTableCsv}
          onExportPdf={handleDownloadTablePdf}
          fullWidthOnMobile
        />

        {canRegisterNcr && <RefundNcrGuidanceNote />}

        <ClientHeader
          data={headerData}
          config={REFUND_HEADER_CONFIG}
          cardSx={MOBILE_CLIENT_HEADER_CARD_SX}
          titleSx={MOBILE_CLIENT_HEADER_TITLE_SX}
          stackSx={MOBILE_CLIENT_HEADER_STACK_SX}
          itemSx={MOBILE_CLIENT_HEADER_ITEM_SX}
          itemIconSx={MOBILE_CLIENT_HEADER_ICON_SX}
          itemIconSize={18}
          itemLabelSx={MOBILE_CLIENT_HEADER_TEXT_SX}
          itemValueSx={MOBILE_CLIENT_HEADER_VALUE_SX}
        />

        <ProgressSummaryCard
          title={REFUND_APPROVAL_TEXT.summaryTitle}
          progressLabel={REFUND_APPROVAL_TEXT.progressLabel}
          progressPercent={progressPercent}
          metrics={progressSummaryMetrics}
          collapsible
          collapseMetricsOnly
          defaultExpanded={false}
          cardSx={MOBILE_SUMMARY_CARD_SX}
          titleSx={MOBILE_SUMMARY_TITLE_SX}
          progressLabelSx={MOBILE_SUMMARY_PROGRESS_LABEL_SX}
          progressPercentSx={MOBILE_SUMMARY_PROGRESS_PERCENT_SX}
          progressBarSx={{ height: 12 }}
          toggleIconSize={22}
          toggleSx={MOBILE_COLLAPSE_TOGGLE_SX}
          collapseOpenLabel="Cerrar resumen de devolución"
          collapseClosedLabel="Abrir resumen de devolución"
        />

        <DetailsSummarySection
          summaryFields={summaryFields}
          extraFields={fieldsToRender}
          summaryTitle={REFUND_APPROVAL_TEXT.detailsTitle}
          summaryOpenLabel={REFUND_APPROVAL_TEXT.openDetails}
          summaryCloseLabel={REFUND_APPROVAL_TEXT.closeDetails}
          sectionHeaderSx={MOBILE_COLLAPSE_HEADER_SX}
          sectionToggleSx={MOBILE_COLLAPSE_TOGGLE_SX}
          summaryDefaultOpen={false}
        />

        <PaymentAttachmentsCard
          images={paymentAttachments.images}
          files={paymentAttachments.files}
          title={UI_TEXT_ATTACHMENTS}
          emptyDescription={REFUND_APPROVAL_TEXT.attachmentsEmpty}
          sectionHeaderSx={MOBILE_COLLAPSE_HEADER_SX}
          sectionToggleSx={MOBILE_COLLAPSE_TOGGLE_SX}
        />

        <Divider
          sx={{
            flexBasis: '100%',
            width: '100%',
            border: 0,
            borderTop: `1px solid ${UI_COLOR_BORDER_DIVIDER}`,
            mt: 3,
            mb: 2
          }}
        />

        <RefundApprovalProductsSectionMobile
          showLineActions={showLineActions}
          isFinalized={isFinalized}
          loading={loading}
          lines={processingLines}
          copyTooltip={copyTooltip}
          onCopyTable={handleCopyTable}
          onDownloadCsv={handleDownloadTableCsv}
          onScan={openScanModal}
          onEditLine={openScanModal}
          onRejectLine={setLineToReject}
        />
      </>
    )}
  </>
)

export default RefundApprovalDetailMobile
